import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import RouteCard from './RouteCard';
import RouteTimesModal from './RouteTimesModal';
import ViolationsModal from './ViolationsModal';
import EventInviteModal from './EventInviteModal';
import EventTeamsSection from './EventTeamsSection';
import './EventView.css';
import { localeFor } from '../utils/timeFormat';
import { startsAfterToday } from '../utils/eventStatus';

const EventView = () => {
  const { t, language, pluralize } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  const [routesPerPage, setRoutesPerPage] = useState(3);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  // Полный журнал нарушений события: релегации и автофиксация контроля веса
  const [showViolations, setShowViolations] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [userActivities, setUserActivities] = useState([]);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descriptionRef = useRef(null);
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const autoScrolledRef = useRef(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  // Функция для получения реального gap между элементами карусели
  const getCarouselGap = () => {
    if (window.innerWidth <= 768) {
      return 0; // На мобильных нет gap
    }
    return 20; // На десктопе gap 20px
  };

  useEffect(() => {
    const handleResize = () => {
      const newRoutesPerPage = window.innerWidth <= 768 ? 1 : window.innerWidth <= 1024 ? 2 : 3;
      
      if (newRoutesPerPage !== routesPerPage) {
        setRoutesPerPage(newRoutesPerPage);
        // Пересчитываем текущую страницу при изменении routesPerPage,
        // но только после того как автопролистывание уже было выполнено
        if (autoScrolledRef.current) {
          setCurrentRouteIndex(prevIndex => {
            const currentPage = Math.floor(prevIndex / routesPerPage);
            const newIndex = currentPage * newRoutesPerPage;
            return Math.min(newIndex, Math.max(0, (event?.routes?.length || 0) - newRoutesPerPage));
          });
        }
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [routesPerPage, event?.routes?.length]);

  useEffect(() => {
    fetchEvent();
    if (user) {
      fetchUserActivities();
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Глобальный обработчик для отпускания кнопки мыши
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        setDragOffset(0);
        if (carouselRef.current) {
          carouselRef.current.style.cursor = 'grab';
        }
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  useEffect(() => {
    if (descriptionRef.current && event?.description) {
      const lineHeight = parseFloat(getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      const lines = height / lineHeight;
      setShowExpandButton(lines > 2);
    }
  }, [event?.description]);

  // silent — обновление данных без блокирующего лоадера (например, после регистрации на гонку)
  const fetchEvent = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`/api/events/${id}`);
      const data = response.data;
      setEvent(data);
      setError(null);

      // Автопролистывание до ближайшего проезда. У открытого турнира расписания
      // нет — прокручивать не к чему, поэтому карусель остается на первой тройке
      if (!silent && data.event_type === 'open_tournament') {
        setCurrentRouteIndex(0);
        autoScrolledRef.current = true;
      } else if (!silent && data.routes && data.routes.length > 0) {
        const now = new Date();
        const currentRPP = window.innerWidth <= 768 ? 1 : window.innerWidth <= 1024 ? 2 : 3;

        // Ищем индекс первого маршрута с ещё не прошедшим ближайшим проездом
        const firstActiveIndex = data.routes.findIndex(route => {
          const nextTime = route.next_start_time;
          return nextTime && new Date(nextTime) >= now;
        });

        let targetIndex;
        if (firstActiveIndex !== -1) {
          // Есть ближайший проезд — прокручиваем к нему
          targetIndex = Math.floor(firstActiveIndex / currentRPP) * currentRPP;
        } else {
          // Все проезды завершены — прокручиваем до конца
          targetIndex = Math.floor((data.routes.length - 1) / currentRPP) * currentRPP;
        }

        setCurrentRouteIndex(targetIndex);
        autoScrolledRef.current = true;
      }
    } catch (err) {
      console.error('Failed to fetch event:', err);
      if (!silent) {
        setError(err.response?.data?.error || 'Failed to load event');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchUserActivities = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`/api/users/${user.id}/activities?event_id=${id}&per_page=1000`);
      setUserActivities(response.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch user activities:', err);
      // Не показываем ошибку пользователю, просто не загружаем активности
      setUserActivities([]);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('events.confirm_delete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/events/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      navigate('/events');
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert(t('events.errors.delete_failed'));
    }
  };

  const nextRoute = () => {
    if (event.routes && currentRouteIndex < event.routes.length - routesPerPage) {
      const nextIndex = currentRouteIndex + routesPerPage;
      // Выравниваем индекс по границе страницы
      const alignedIndex = Math.floor(nextIndex / routesPerPage) * routesPerPage;
      setCurrentRouteIndex(Math.min(alignedIndex, Math.floor((event.routes.length - 1) / routesPerPage) * routesPerPage));
    }
  };

  const prevRoute = () => {
    if (currentRouteIndex > 0) {
      const prevIndex = currentRouteIndex - routesPerPage;
      // Выравниваем индекс по границе страницы
      const alignedIndex = Math.floor(Math.max(0, prevIndex) / routesPerPage) * routesPerPage;
      setCurrentRouteIndex(alignedIndex);
    }
  };

  const goToRoute = (index) => {
    // Убеждаемся, что индекс выровнен по границе страницы
    const alignedIndex = Math.floor(index / routesPerPage) * routesPerPage;
    setCurrentRouteIndex(alignedIndex);
  };

  const handleMouseDown = (e) => {
    if (!carouselRef.current) return;
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(currentRouteIndex);
    setDragOffset(0);
    carouselRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 0.5; // Уменьшаем множитель для более плавного движения
    setDragOffset(walk);
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    setIsDragging(false);
    if (carouselRef.current) {
      carouselRef.current.style.cursor = 'grab';
    }

    // Определяем направление и величину свайпа
    const threshold = 80; // Увеличиваем порог для переключения
    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && currentRouteIndex > 0) {
        // Свайп вправо - предыдущая страница
        prevRoute();
      } else if (dragOffset < 0 && currentRouteIndex < event.routes.length - routesPerPage) {
        // Свайп влево - следующая страница
        nextRoute();
      }
    }
    setDragOffset(0);
  };

  const handleTouchStart = (e) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX);
    setScrollLeft(currentRouteIndex);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX;
    const walk = (x - startX) * 0.9;
    setDragOffset(walk);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 50;
    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && currentRouteIndex > 0) {
        prevRoute();
      } else if (dragOffset < 0 && currentRouteIndex < event.routes.length - routesPerPage) {
        nextRoute();
      }
    }
    setDragOffset(0);
  };

  const handleOpenSchedule = (routeName, times, routeId) => {
    setSelectedRouteId(routeId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRouteId(null);
  };

  // Регистрация на конкретную гонку этапа (события с лимитом гонок на этапе)
  const handleRegisterRace = async (eventRouteId) => {
    await axios.post(`/api/events/${id}/race_registrations`, { event_route_id: eventRouteId });
    await fetchEvent({ silent: true });
  };

  const handleUnregisterRace = async (eventRouteId) => {
    await axios.delete(`/api/events/${id}/race_registrations/${eventRouteId}`);
    await fetchEvent({ silent: true });
  };

  // Название этапа без суффикса " | Race A" — как на карточке маршрута
  const getRouteDisplayName = (route) => {
    return (route.name || 'Unnamed Route').replace(/\s*\|\s*Race\s+[A-Z]$/i, '');
  };

  // Данные выбранного этапа берем из event, чтобы модалка обновлялась после регистрации на гонку
  const selectedRoute = event?.routes?.find(route => route.route_id === selectedRouteId) || null;
  const selectedWildCard = event?.user_wild_cards?.find(wc => wc.route_id === selectedRouteId) || null;

  const getEventTypeLabel = (type) => {
    return t(`events.types.${type}`);
  };

  const getStatusLabel = (status) => {
    return t(`events.statuses.${status}`);
  };

  // Период проведения турнира в местном времени зрителя
  const formatPeriod = (event) => {
    const format = (value) => new Date(value).toLocaleString(localeFor(language, 'en-GB'), {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    return [
      event.starts_at && t('route_card.open_window_from').replace('{{from}}', format(event.starts_at)),
      event.ends_at && t('route_card.open_window_until').replace('{{until}}', format(event.ends_at))
    ].filter(Boolean).join(' ');
  };

  const getEventStatus = (event) => {
    // У открытого турнира расписания нет — его состояние задает период проведения
    if (event.event_type === 'open_tournament') {
      const startsAt = event.starts_at ? new Date(event.starts_at) : null;
      const endsAt = event.ends_at ? new Date(event.ends_at) : null;

      if (endsAt && endsAt < new Date()) {
        return { status: 'completed', label: t('events.event_status.completed') };
      }
      if (startsAt && startsAt > new Date()) {
        return { status: 'planned', label: t('events.event_status.planned') };
      }
      return { status: 'active', label: t('events.event_status.active') };
    }

    if (!event.event_routes || event.event_routes.length === 0) {
      return { status: 'no_routes', label: '-' };
    }

    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    // Маршруты открытого турнира старта не имеют: пока такой
    // маршрут в составе события, событие идет
    const eventRoutes = event.event_routes.filter(er => er.start_date_time_utc);

    if (eventRoutes.length === 0) {
      return { status: 'active', label: t('events.event_status.active') };
    }
    
    const pastRoutes = eventRoutes.filter(er => {
      const startDate = new Date(er.start_date_time_utc);
      return startDate < oneHourLater;
    });
    const futureRoutes = eventRoutes.filter(er => startsAfterToday(er.start_date_time_utc));

    console.log(pastRoutes.length, eventRoutes.length)

    if (pastRoutes.length === eventRoutes.length) {
      return { status: 'completed', label: t('events.event_status.completed') };
    }

    if (futureRoutes.length === eventRoutes.length) {
      return { status: 'planned', label: t('events.event_status.planned') };
    }

    return { status: 'active', label: t('events.event_status.active') };
  };

  const isRegistrationOpen = (event) => {
    if (event.long_event) {
      return true;
    }

    const status = getEventStatus(event).status;

    if (status === 'completed') {
      return false;
    }

    // Если registration_closes_hours_before не установлено (null), регистрация открыта всегда
    if (event.registration_closes_hours_before === null || event.registration_closes_hours_before === undefined) {
      return true;
    }

    // Находим самый ранний проезд
    const now = new Date();
    const earliestRoute = event.event_routes.filter(er => er.start_date_time_utc).reduce((earliest, route) => {
      const routeDate = new Date(route.start_date_time_utc);
      return !earliest || routeDate < new Date(earliest.start_date_time_utc) ? route : earliest;
    }, null);

    // В открытом турнире регистрация живет по периоду проведения, а он уже
    // проверен через статус события выше
    if (!earliestRoute) return event.event_type === 'open_tournament';

    const firstRideTime = new Date(earliestRoute.start_date_time_utc);
    const closeTime = new Date(firstRideTime.getTime() - event.registration_closes_hours_before * 60 * 60 * 1000);

    // Регистрация открыта, если текущее время меньше времени закрытия
    return now < closeTime;
  };

  if (loading) {
    return (
      <div className="event-view-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-view-container">
        <div className="error">{error}</div>
        <button onClick={() => navigate('/events')} className="btn-back">
          {t('common.back')}
        </button>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="event-view-container">
      <div className="event-view-header">
        <button onClick={() => navigate('/events')} className="btn-back">
          ← {t('common.back')}
        </button>
        {/* Соорганизаторам доступна только страница добавления гонок */}
        {user && (user.admin || user.id === event.user_id || event.co_organizers?.some(o => o.id === user.id)) && (
          <div className="event-actions">
            {event.can_invite_participants && (
              <button
                onClick={() => setShowInvite(true)}
                className="btn-invite"
              >
                {t('eventInvite.button')}
              </button>
            )}
            {event.can_view_violations && (
              <button
                onClick={() => setShowViolations(true)}
                className="btn-violations"
              >
                {t('violations.title')}
              </button>
            )}
            <button
              onClick={() => navigate(`/events/${id}/edit`)}
              className="btn-edit"
            >
              {t('common.edit')}
            </button>
            {(user.admin || user.id === event.user_id) && (
              <button
                onClick={handleDelete}
                className="btn-delete"
              >
                {t('common.delete')}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="event-view-content">
        <div className="event-main-info">
          <div className="event-left-section">
            <div className="event-image-container">
              {event.image ? (
                <img src={event.image} alt={event.name} className="event-image" />
              ) : (
                <div className="event-image-placeholder-large">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </div>
              )}
              {event.routes.length > 0 && !isRegistrationOpen(event) && (
                <span className="registration-closed-badge on-image">
                  {t('events.registration_closed')}
                </span>
              )}
            </div>
            
            <div className="event-image-info">
              <h1 className="event-title">{event.name}</h1>
              
              <div className="event-badges">
                {event.hidden && (
                  <span className="event-hidden-badge">{t('events.hidden_badge')}</span>
                )}
                <span className={`event-type-badge event-type-${event.event_type}`}>
                  {getEventTypeLabel(event.event_type)}
                </span>
                {event.status === 'official' ? (
                  <div className="event-author-badge event-author-official">
                    <span>{t('events.by_user')}</span>
                    <img src="/rouvy.svg" alt="ROUVY" className="rouvy-icon" />
                  </div>
                ) : event.user ? (
                  <div className="event-author-badge event-author-custom">
                    <span>{t('events.by_user')} {event.user.nickname || `${event.user.first_name} ${event.user.last_name}`}</span>
                  </div>
                ) : null}
                <span className={`event-status-badge event-status-${event.status}`}>
                  {getStatusLabel(event.status)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="event-details">
            {event.description && (
              <div className="event-description">
                <h3>{t('events.fields.description')}</h3>
                <div className="description-wrapper">
                  <p 
                    ref={descriptionRef}
                    className={`description-text ${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}
                  >
                    {event.description}
                  </p>
                  {showExpandButton && !isDescriptionExpanded && (
                    <button 
                      className="expand-button inline"
                      onClick={() => setIsDescriptionExpanded(true)}
                    >
                      {t('common.showMore')}
                    </button>
                  )}
                </div>
                {showExpandButton && isDescriptionExpanded && (
                  <button 
                    className="expand-button below"
                    onClick={() => setIsDescriptionExpanded(false)}
                  >
                    {t('common.showLess')}
                  </button>
                )}
              </div>
            )}

            <div className="event-info-grid">
              {/* Период проведения открытого турнира: внутри него закрывают маршруты */}
              {event.event_type === 'open_tournament' && (event.starts_at || event.ends_at) && (
                <div className="event-info-item">
                  <span className="event-info-label">{t('events.fields.period')}:</span>
                  <span className="event-info-value">{formatPeriod(event)}</span>
                </div>
              )}

              <div className="event-info-item">
                <span className="event-info-label">{t('events.fields.individual_competition')}:</span>
                <span className="event-info-value">
                  {event.individual_competition ? t('common.yes') : t('common.no')}
                </span>
              </div>
              
              <div className="event-info-item">
                <span className="event-info-label">{t('events.fields.team_competition')}:</span>
                <span className="event-info-value">
                  {event.team_competition ? t('common.yes') : t('common.no')}
                </span>
              </div>

              {event.max_scores && (
                <div className="event-info-item">
                  <span className="event-info-label">{t('events.fields.max_scores')}:</span>
                  <span className="event-info-value">{event.max_scores}</span>
                </div>
              )}

              {event.scores_step && (
                <div className="event-info-item">
                  <span className="event-info-label">{t('events.fields.scores_step')}:</span>
                  <span className="event-info-value">{event.scores_step}</span>
                </div>
              )}

              {event.team_competition && event.members_accounting && (
                <div className="event-info-item">
                  <span className="event-info-label">{t('events.fields.members_accounting')}:</span>
                  <span className="event-info-value">{event.members_accounting}</span>
                </div>
              )}

              {event.team_competition && event.max_team_members && (
                <div className="event-info-item">
                  <span className="event-info-label">{t('events.fields.max_team_members')}:</span>
                  <span className="event-info-value">{event.max_team_members || '---'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {event.routes && event.routes.length > 0 && (
          <div className="event-routes-section">
            <h2 className="routes-section-title">
              {t('events.routes')}
            </h2>
            
            <div className="routes-carousel-container">
              <div className="routes-carousel">
                {event.routes.length > 1 && currentRouteIndex > 0 && (
                  <button 
                    className="carousel-nav carousel-nav-prev" 
                    onClick={prevRoute}
                    aria-label={t('a11y.previousRoute')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}

                <div 
                  className="routes-carousel-track"
                  ref={carouselRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                  <div 
                    className="routes-carousel-inner"
                    style={{ 
                      transform: `translateX(calc(-${currentRouteIndex * (100 / routesPerPage)}% - ${currentRouteIndex * (getCarouselGap() / routesPerPage)}px + ${dragOffset}px))`,
                      transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                    }}
                  >
                    {event.routes.map((route, index) => (
                      <div key={route.route_id || route.id} className="carousel-route-item">
                        <RouteCard
                          event_route={route}
                          onOpenSchedule={handleOpenSchedule}
                          event={event}
                          onExtraRacesChanged={() => fetchEvent({ silent: true })}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {event.routes.length > routesPerPage && currentRouteIndex < event.routes.length - routesPerPage && (
                  <button 
                    className="carousel-nav carousel-nav-next" 
                    onClick={nextRoute}
                    aria-label={t('a11y.nextRoute')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              {Math.ceil(event.routes.length / routesPerPage) > 1 && (
                <div className="carousel-indicators">
                  {Array.from({ length: Math.ceil(event.routes.length / routesPerPage) }).map((_, pageIndex) => (
                    <button
                      key={pageIndex}
                      className={`carousel-indicator ${pageIndex * routesPerPage === currentRouteIndex ? 'active' : ''}`}
                      onClick={() => goToRoute(pageIndex * routesPerPage)}
                      aria-label={`Go to page ${pageIndex + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Команды под событие: только при командном зачете */}
        {event.team_competition && <EventTeamsSection event={event} user={user} />}
      </div>

      {selectedRoute && (
        <RouteTimesModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          eventId={event.id}
          routeName={getRouteDisplayName(selectedRoute)}
          times={selectedRoute.all_times || []}
          userActivities={userActivities}
          wildCard={selectedWildCard}
          racesPerStage={event.races_per_stage}
          stage={selectedRoute.user_stage}
          canRegister={Boolean(user && event.user_registration?.registered)}
          onRegister={handleRegisterRace}
          onUnregister={handleUnregisterRace}
          userExtraRaces={(event.user_extra_races || []).filter(race => race.route_id === selectedRoute?.route_id)}
        />
      )}

      {showViolations && (
        <ViolationsModal event={event} onClose={() => setShowViolations(false)} />
      )}

      {showInvite && (
        <EventInviteModal event={event} onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
};

export default EventView;